import { createElement, useCallback, useState } from 'react';

import { notifications } from 'ui';

import { Printty } from '../../../../../packages/Printty/printty';
import { EventTicketPDF } from './EventTicketPDF';
import { PdfInfoBox } from './PdfInfoBox';
import { EventTicketInfo } from './eventTicketsPdf.types';

type useCreateTicketPdf = {
  eventName: string;
  eventStartDate?: string;
  location?: string;
};

type EventPDfList = EventTicketInfo[];

const useCreateTicketPdf = ({ eventName, eventStartDate, location }: useCreateTicketPdf) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    savePdf,
    targetReactRef,
    elementClassId,
    isLoading: isPdfLoading,
  } = Printty.usePdf({
    HeaderInfoBoxElement: <PdfInfoBox eventStartDate={eventStartDate} eventLocation={location} />,
    headerProps: {
      title: 'Event Tickets',
      subTitle: eventName,
    },
    orientation: 'portrait',
    gap: 16,
  });

  /**
   * Generates a list of React components for the EventTicketPDF
   * @param props - The props for the EventTicketPDF
   * @returns A React component for the EventTicketPDF
   */
  const generatePdfComponent = useCallback(
    (eventList: EventPDfList) => {
      const pdfComponents = eventList.map((event, idx) =>
        createElement(EventTicketPDF, {
          key: idx,
          ...event,
          eventTitle: event.subtitle || event.title,
          className: elementClassId,
        })
      );
      return pdfComponents;
    },
    [elementClassId]
  );

  /**
   * Creates a PDF of the ticket
   * @param ticketUrl - The URL of the ticket
   */
  const createTicketPdf = async (eventList: EventPDfList) => {
    setIsLoading(true);

    try {
      const component = generatePdfComponent(eventList);
      targetReactRef.current = component;
      await savePdf({ fileName: `${eventName}` });
    } catch {
      notifications.show({
        message: 'Failed to create ticket PDF',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { createTicketPdf, isLoading: isLoading || isPdfLoading };
};

export { useCreateTicketPdf };
